class PredictiveSearch extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      searchQuery: "[data-query]",
      searchMessage: "[data-message]",
      input: "[data-search-input]",
      submit: 'button[type="submit"]',
      loading: "[data-spinner]",
      clear: "[data-clear-search]",
      popularSearchItems: ["[data-ps-item]"],
      moreResultIcon: "[data-more-result-icon]",
    };

    this.container = document.querySelector("[data-search-popup]");
    this.domNodes = queryDomNodes(this.selectors, this.container);
    this.cachedResults = {};
    this.transitionDuration = 300;

    this.input = this.querySelector('input[type="search"]');
    this.predictiveSearchResults = this.querySelector("[data-predictive-search]");
    this.searchPopupResults = this.querySelector(".m-search-popup__result");
    this.popularSearches = this.querySelector("[data-popular-searches]");
    this.searchCount = this.querySelector("[data-search-count]");

    this.setupEventListeners();
  }

  setPopularSearchesLink = () => {
    const { popularSearchItems } = this.domNodes;
    popularSearchItems.forEach((item) => {
      item.href = createSearchLink(item.dataset.psQuery);
    });
  };

  setupEventListeners() {
    const { clear } = this.domNodes;

    this.querySelector("form.m-search-form")?.addEventListener(
      "submit",
      this.onFormSubmit.bind(this)
    );

    this.input?.addEventListener(
      "input",
      debounce((e) => this.onChange(e), 300)
    );

    clear?.addEventListener("click", this.onClearSearch.bind(this));

    MinimogEvents.subscribe(
      MinimogTheme.pubSubEvents.closeSearchPopup,
      () => this.close(true)
    );
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    const query = this.getQuery();
    if (query.length) {
      this.getSearchResults(query);
    } else {
      this.close(true);
    }
  }

  onFormSubmit(e) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) {
      e.preventDefault();
    }
  }

  onClearSearch(e) {
    e.preventDefault();
    this.input.value = "";
    this.onChange();
  }

  getSearchResults(query) {
    const cacheKey = query.trim().toLowerCase().replace(/\s+/g, "-");

    this.toggleSpinnerLoading(true);
    this.toggleClearSearch(false);

    if (this.cachedResults[cacheKey]) {
      this.renderSearchResults(this.cachedResults[cacheKey]);
      return;
    }

    const url = new URL(
      window.MinimogSettings.routes.predictive_search_url,
      window.location.origin
    );

    url.searchParams.set("q", query);
    url.searchParams.set("resources[type]", "product,collection,article");
    url.searchParams.set("resources[limit]", "10");
    url.searchParams.set("resources[limit_scope]", "each");
    url.searchParams.set("resources[options][fields]", "title,product_type,vendor,tag,variants.sku,variants.title");
    url.searchParams.set("section_id", "predictive-search");

    fetch(url)
      .then((res) => {
        if (!res.ok) throw new Error("Network error");
        return res.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, "text/html");
        const content = parsed
          .querySelector("#shopify-section-predictive-search")
          ?.innerHTML;

        if (!content) throw new Error("No section content returned");

        this.cachedResults[cacheKey] = content;
        this.renderSearchResults(content);
      })
      .catch((err) => {
        console.error("[PREDICTIVE ERROR]", err);
        this.close(true);
      });
  }

  renderSearchResults(content) {
    this.predictiveSearchResults.innerHTML = content;
    this.setAttribute("results", "true");

    const noResultsEl = this.predictiveSearchResults.querySelector("[data-no-results]");
    const renderQueryEl = noResultsEl?.querySelector("[data-render-query]");

    if (noResultsEl && renderQueryEl) {
      const hasResults = this.querySelector("[data-search-items-wrapper]")?.childElementCount > 0;

      renderQueryEl.textContent = this.input.value;
      noResultsEl.classList.toggle("m:hidden", hasResults);
    }

    this.renderSearchQueryAndMessage(
      this.querySelector("[data-search-items-wrapper]")?.childElementCount > 0
    );

    this.toggleSpinnerLoading(false);
    this.toggleClearSearch(true);
    this.open();
  }

  renderSearchQueryAndMessage(hasResults) {
    const { input, searchQuery, searchMessage, moreResultIcon } = this.domNodes;
    const queryValue = input.value;
    const resultsTitle = searchMessage.dataset.resultsTitle;

    searchQuery.textContent = queryValue;
    this.predictiveSearchResults.classList.remove("m:hidden");
    this.searchPopupResults.classList.remove("m:hidden");

    if (hasResults) {
      searchMessage.textContent = resultsTitle;
      moreResultIcon.classList.add("m:hidden");
    } else {
      searchMessage.textContent = searchMessage.dataset.resultsTitle;
      moreResultIcon.classList.remove("m:hidden");
    }
  }

  toggleSpinnerLoading(isLoading) {
    const { loading, submit } = this.domNodes;
    submit.style.visibility = isLoading ? "hidden" : "visible";
    loading.style.visibility = isLoading ? "visible" : "hidden";
  }

  toggleClearSearch(isVisible) {
    const { clear } = this.domNodes;
    clear.style.visibility = isVisible ? "visible" : "hidden";
  }

  open() {
    this.setAttribute("open", "true");
    this.input.setAttribute("aria-expanded", "true");
    this.isOpen = true;

    this.popularSearches?.classList.add("m:hidden");
    this.searchCount?.classList.remove("m:hidden");

    // Hide Smile and Chat widgets
    document.querySelector('.smile-launcher-frame-container')?.classList.add("m:hidden");
    document.querySelector('#gorgias-chat-container')?.classList.add("m:hidden");
  }

  close(clearInput = false) {
    if (clearInput) {
      this.input.value = "";
      this.removeAttribute("results");
      this.toggleClearSearch(false);
      this.resultsMaxHeight = false;
    }

    this.removeAttribute("open");
    this.predictiveSearchResults.classList.add("m:hidden");
    this.searchPopupResults.classList.add("m:hidden");
    this.popularSearches?.classList.remove("m:hidden");

    // Show Smile and Chat widgets
    document.querySelector('.smile-launcher-frame-container')?.classList.remove("m:hidden");
    document.querySelector('#gorgias-chat-container')?.classList.remove("m:hidden");
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight -
      document.querySelector("predictive-search").getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }
}

customElements.define("predictive-search", PredictiveSearch);

if (!customElements.get("m-predictive-slider")) {
  class PredictiveSlider extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.selectors = {
        slideControls: ".m-slider-controls",
        slideContainer: ".m-mixed-layout__wrapper",
      };

      this.domNodes = queryDomNodes(this.selectors, this);
      this.enableSlider = this.dataset.enableSlider === "true";
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = parseInt(this.dataset.items, 10) || 4;
      this.mobileDisableSlider = this.dataset.mobileDisableSlider === "true";

      this.initByScreenSize();

      document.addEventListener("matchMobile", () => this.initByScreenSize());
      document.addEventListener("unmatchMobile", () => this.initByScreenSize());
    }

    initByScreenSize() {
      if (!this.enableSlider) return;

      const { slideContainer, slideControls } = this.domNodes;

      if (MinimogTheme.config.mqlMobile && this.mobileDisableSlider) {
        slideControls?.classList.add("m:hidden");
        slideContainer?.classList.remove("swiper-container");
        this.swiper?.destroy(false, true);
      } else {
        slideControls?.classList.remove("m:hidden");
        this.initSlider();
      }
    }

    initSlider() {
      const container = this.domNodes.slideContainer;
      const controls = this.querySelector(".m-slider-controls");
      const prevBtn = controls?.querySelector(".m-slider-controls__button-prev");
      const nextBtn = controls?.querySelector(".m-slider-controls__button-next");
      const totalSlides = container?.querySelector(".swiper-wrapper")?.childElementCount;

      container?.classList.add("swiper-container");

      this.slider = new MinimogLibs.Swiper(container, {
        slidesPerView: 2.25,
        spaceBetween: 15,
        loop: false,
        watchOverflow: true,
        freeMode: false,
        slidesOffsetAfter: 0,
        resistanceRatio: 0.33,
        grabCursor: true,
        simulateTouch: true,
        threshold: 2,
        showNavigation: true,
        observer: true,
        observeParents: true,
        mousewheel: {
          forceToAxis: true,
          releaseOnEdges: true,
        },
        breakpoints: {
          768: { slidesPerView: "auto" },
        },
        pagination: this.showPagination
          ? {
              el: this.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : undefined,
        on: {
          init: () => {
            setTimeout(() => {
              const sampleImage = this.querySelector(".m-image") || this.querySelector(".m-placeholder-svg");
              if (sampleImage && controls) {
                const offset = sampleImage.clientHeight;
                controls.style.setProperty("--offset-top", `${parseInt(offset, 10) / 2}px`);
                prevBtn?.classList.remove("m:hidden");
                nextBtn?.classList.remove("m:hidden");
              }
            }, 200);
          },
          breakpoint: (swiper, settings) => {
            if (!controls) return;
            const { slidesPerView } = settings;
            if (totalSlides > slidesPerView) {
              controls.classList.remove("m:hidden");
              swiper.allowTouchMove = true;
            } else {
              controls.classList.add("m:hidden");
              swiper.allowTouchMove = false;
            }
          },
        },
      });

      if (this.slider && this.showNavigation) {
        prevBtn?.addEventListener("click", () => this.slider.slidePrev());
        nextBtn?.addEventListener("click", () => this.slider.slideNext());
      }

      this.swiper = container?.swiper;
    }
  }

  customElements.define("m-predictive-slider", PredictiveSlider);
}

const originalRender = PredictiveSearch.prototype.renderSearchResults;
PredictiveSearch.prototype.renderSearchResults = function (html) {
  originalRender.call(this, html);

  // Initialize sliders if not already initialized
  this.predictiveSearchResults
    .querySelectorAll("m-predictive-slider")
    .forEach((slider) => {
      if (!slider.swiper && typeof slider.connectedCallback === "function") {
        slider.connectedCallback();
      }
    });

  // Inject slider controls if missing
  this.predictiveSearchResults
    .querySelectorAll(".m-search-result__products")
    .forEach((wrapper) => {
      const headingContainer = wrapper.querySelector(".m-predictive-search-heading");
      if (!headingContainer || headingContainer.querySelector(".m-slider-controls")) return;

      const controls = document.createElement("div");
      controls.className = "m-slider-controls";
      controls.innerHTML = `
        <button class="m-slider-controls__button m-slider-controls__button-prev" aria-label="Previous">&larr;</button>
        <button class="m-slider-controls__button m-slider-controls__button-next" aria-label="Next">&rarr;</button>
      `;
      headingContainer.appendChild(controls);
    });

  // Re-initialize sliders
  this.predictiveSearchResults
    .querySelectorAll("m-predictive-slider")
    .forEach((slider) => {
      if (!slider.swiper && typeof slider.connectedCallback === "function") {
        slider.connectedCallback();
      }
    });

  // Bind navigation buttons
  this.predictiveSearchResults
    .querySelectorAll("m-predictive-slider")
    .forEach((slider) => {
      const wrapper = slider.closest(".m-search-result__products");
      const prevBtn = wrapper?.querySelector(".m-slider-controls__button-prev");
      const nextBtn = wrapper?.querySelector(".m-slider-controls__button-next");

      if (!slider.swiper || !prevBtn || !nextBtn) return;

      prevBtn.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        slider.swiper.slidePrev();
      });

      nextBtn.addEventListener("pointerdown", (e) => {
        e.stopPropagation();
        slider.swiper.slideNext();
      });
    });

  // Image hover swapping for sibling thumbnails
  document
    .querySelectorAll(".search-result-slide .sibling-product-list a[data-sibling-featured-image]")
    .forEach((link) => {
      if (link.dataset.imgSwapBound) return;
      link.dataset.imgSwapBound = "true";

      const card = link.closest(".m-product-card");
      const mainImg = card?.querySelector(".m-product-card__main-image img");
      if (!card || !mainImg) return;

      if (!mainImg.dataset.originalSrc) {
        mainImg.dataset.originalSrc = mainImg.src;
        mainImg.dataset.originalSrcset = mainImg.srcset || "";
        mainImg.dataset.originalSizes = mainImg.sizes || "";
      }

      link.addEventListener("pointerenter", () => {
        let newSrc = link.getAttribute("data-sibling-featured-image") || "";
        if (newSrc.startsWith("//")) newSrc = "https:" + newSrc;

        try {
          const url = new URL(newSrc);
          url.searchParams.set("width", "535");
          newSrc = url.toString();
        } catch (e) {}

        card.classList.remove("m-product-card--show-second-img");
        mainImg.src = newSrc;
        mainImg.removeAttribute("srcset");
        mainImg.removeAttribute("sizes");
      });

      link.addEventListener("pointerleave", () => {
        card.classList.add("m-product-card--show-second-img");
        mainImg.src = mainImg.dataset.originalSrc;
        mainImg.srcset = mainImg.dataset.originalSrcset;
        mainImg.sizes = mainImg.dataset.originalSizes;
      });
    });
};
